/**
 * Sheet lateral de Preferências de Notificação do usuário autenticado.
 *
 * Substitui a antiga rota /tenant/settings/notifications, deslocando a
 * configuração (que é por usuário) para fora do escopo de "configurações do
 * tenant". Aberto a partir do menu do avatar (UserMenu).
 *
 * Reaproveita integralmente o hook useNotificationSettings e os componentes
 * NotificationMatrix / NotificationDndCard.
 *
 * @version 1.1.0 - 2026-06-05 — Adicionada Seção C "Push neste dispositivo"
 *                                (migrado do antigo NotificationPreferencesPopover)
 * @version 1.0.0 - 2026-06-05 — Migração da página dedicada para Sheet
 */
import { Bell } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { useNotificationSettings } from '@/hooks/useNotificationSettings';
import { NotificationMatrix } from './NotificationMatrix';
import { NotificationDndCard } from './NotificationDndCard';
import { PushNotificationToggle } from './PushNotificationToggle';

interface NotificationPreferencesSheetProps {
  /** Controla a visibilidade do Sheet. */
  open: boolean;
  /** Callback disparado ao abrir/fechar o Sheet. */
  onOpenChange: (open: boolean) => void;
}

export function NotificationPreferencesSheet({
  open,
  onOpenChange,
}: NotificationPreferencesSheetProps) {
  const {
    data, loading, saving, matrix, setPreference, setDnd,
  } = useNotificationSettings();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl p-0 flex flex-col gap-0"
      >
        {/* Header compacto fixo no topo */}
        <SheetHeader className="px-6 py-4 border-b border-border/60 space-y-1">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Bell className="h-4 w-4 text-primary" />
            </div>
            <div className="text-left">
              <SheetTitle className="text-base font-semibold tracking-tight">
                Preferências de Notificação
              </SheetTitle>
              <SheetDescription className="text-xs">
                Escolha como receber cada tipo de notificação e configure o Não Perturbe.
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {/* Corpo rolável */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* Seção A — Matriz Módulo × Canal */}
          <section className="space-y-3">
            <div>
              <h2 className="text-sm font-semibold text-foreground/90">Canais por módulo</h2>
              <p className="text-xs text-muted-foreground">
                Cada célula define se o módulo entrega notificações naquele canal.
                Mudanças são salvas automaticamente.
              </p>
            </div>
            {loading || !data ? (
              <Skeleton className="h-72 w-full rounded-xl" />
            ) : (
              <NotificationMatrix
                modules={data.modules}
                channels={data.channels}
                preferences={data.preferences}
                matrix={matrix}
                onToggle={setPreference}
              />
            )}
          </section>

          {/* Seção B — Não Perturbe */}
          <section className="space-y-3">
            <div>
              <h2 className="text-sm font-semibold text-foreground/90">Não Perturbe</h2>
              <p className="text-xs text-muted-foreground">
                Janela diária em que canais externos ficam silenciados. O Centro de
                Notificações continua recebendo tudo.
              </p>
            </div>
            {loading || !data ? (
              <Skeleton className="h-72 w-full rounded-xl" />
            ) : (
              <NotificationDndCard
                value={data.dnd}
                channels={data.channels}
                onSave={setDnd}
                saving={saving}
              />
            )}
          </section>

          {/* Seção C — Push neste dispositivo (controla permissão + subscription do navegador atual) */}
          <section className="space-y-3">
            <div>
              <h2 className="text-sm font-semibold text-foreground/90">Push neste dispositivo</h2>
              <p className="text-xs text-muted-foreground">
                Ative para receber notificações push neste navegador. A permissão é por dispositivo.
              </p>
            </div>
            <div className="rounded-xl border border-border/60 p-3">
              <PushNotificationToggle />
            </div>
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
}
